import { SCRIPT_MAX_BYTES } from '@noconflict/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { orderErrorMessage } from '../../../entities/order';
import { uploadScriptObject } from '../../../entities/project-file';
import { analyseScript } from '../../../entities/script-analysis';
import { ApiRequestError } from '../../../shared/api/http';
import { supabase } from '../../../shared/api/supabase';

/** Why a file was turned away: these are about the file, so the same file is not offered again. */
export type ScriptRefusal = 'not_pdf' | 'too_large' | 'unpriceable';

export type UploadState =
  | { phase: 'idle' }
  /** The bytes are on their way to storage. */
  | { phase: 'uploading' }
  /** The worker is measuring and reading the script. */
  | { phase: 'reading' }
  | { phase: 'refused'; reason: ScriptRefusal }
  /** Something of ours failed (the network, the extractor): the same file can be tried again. */
  | { phase: 'failed'; message: string };

const REFUSALS: Record<string, ScriptRefusal> = {
  script_unreadable: 'unpriceable',
  script_too_long: 'unpriceable',
  not_a_pdf: 'not_pdf',
  invalid_file: 'not_pdf',
  file_too_large: 'too_large',
};

/** What a script changes besides its own analysis: the details read from it, its file row, the order's length. */
const SCRIPT_QUERIES = ['script-project', 'project-file', 'order', 'projects'];

const looksLikePdf = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

/**
 * Uploads a script and waits while it is analysed. The file goes to storage under a fresh id (its own name never
 * forms a path), then the worker registers, measures and reads it; once that answers, the queries that show the
 * script are refreshed and the form can open. A file that is no use is refused with a reason; a failure of ours
 * keeps the file so it can be tried again.
 */
export function useUploadScript(projectId: string) {
  const qc = useQueryClient();
  const [state, setState] = useState<UploadState>({ phase: 'idle' });
  const last = useRef<File | null>(null);

  const upload = useCallback(
    async (file: File): Promise<boolean> => {
      last.current = file;
      if (!looksLikePdf(file)) {
        setState({ phase: 'refused', reason: 'not_pdf' });
        return false;
      }
      if (file.size > SCRIPT_MAX_BYTES) {
        setState({ phase: 'refused', reason: 'too_large' });
        return false;
      }
      try {
        setState({ phase: 'uploading' });
        const fileId = crypto.randomUUID();
        await uploadScriptObject(supabase, projectId, fileId, file);
        setState({ phase: 'reading' });
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) throw new Error('forbidden');
        await analyseScript(token, projectId, { fileId, filename: file.name });
        // The analysis is refreshed last: the form opens when it arrives, and by then the details it is filled
        // from are already the new ones.
        await Promise.all(SCRIPT_QUERIES.map((key) => qc.invalidateQueries({ queryKey: [key] })));
        await qc.invalidateQueries({ queryKey: ['script-analysis'] });
        setState({ phase: 'idle' });
        return true;
      } catch (e) {
        const reason = e instanceof ApiRequestError ? REFUSALS[e.code] : undefined;
        setState(
          reason
            ? { phase: 'refused', reason }
            : { phase: 'failed', message: orderErrorMessage(e) },
        );
        return false;
      }
    },
    [projectId, qc],
  );

  /** Tries the last file again, after a failure that was not about the file. */
  const retry = useCallback(
    () => (last.current ? upload(last.current) : Promise.resolve(false)),
    [upload],
  );

  return { state, upload, retry, busy: state.phase === 'uploading' || state.phase === 'reading' };
}
